// src/components/dashboard/SearchConsoleCard.tsx
//
// UPDATED — three fixes/additions, all confirmed against a real live
// bug report ("OAuth sits in this dashboard page") and the actual
// deployed dashboardRouter (routes/dashboard.ts):
//
// 1. handleConnect's URL — CONFIRMED CORRECT AS ORIGINALLY WRITTEN:
//    /dashboard/auth/gsc?entityUid=... matches dashboardRouter.get(
//    '/auth/gsc', ...), which reads req.query.entityUid. An earlier
//    pass in this session incorrectly "fixed" this to
//    /dashboard/auth/gsc/connect?uid=..., which does not match any
//    real route — that change is NOT applied here; the original path/
//    param combination was right all along.
//
// 2. Reads gsc_connected / gsc_error query params on mount. The
//    backend callback (routes/dashboard.ts) now redirects to
//    /dashboard/profile with one of these set, instead of leaving the
//    user on a bare error page or a placeholder stub (the actual root
//    cause of the original bug report — the callback was redirecting
//    to /dashboard/settings, unrelated to this card).
//
// 3. Disconnect button added — calls the new POST
//    /dashboard/auth/gsc/disconnect endpoint via authedFetch, which
//    resolves identity from the bearer token, not a uid param.

import React, { useEffect, useState } from 'react';
import { authedFetch, authedFetchRaw } from '../../services/dashboardApi';
import { Button } from '../ui/button';
import { GlobeIcon, ExternalLinkIcon, Loader2Icon, XIcon } from 'lucide-react';

function readQueryParam(name: string): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get(name);
}

// Strips the gsc_connected/gsc_error params from the URL after reading
// them, so a page refresh doesn't keep re-showing a stale status
// message from a prior OAuth round trip.
function clearGscQueryParams() {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.delete('gsc_connected');
  url.searchParams.delete('gsc_error');
  window.history.replaceState({}, '', url.toString());
}

export function SearchConsoleCard() {
  const [gscState, setGscState] = useState<any>(null);
  const [entityUid, setEntityUid] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    // Surface the result of a just-completed OAuth round trip, if any,
    // before the status fetch below.
    const connectedParam = readQueryParam('gsc_connected');
    const errorParam = readQueryParam('gsc_error');

    if (connectedParam) {
      setStatusMessage({ kind: 'success', text: 'Search Console connected successfully.' });
      clearGscQueryParams();
    } else if (errorParam) {
      const friendly = errorParam === 'access_denied'
        ? 'You declined the Google consent screen — Search Console was not connected.'
        : `Connection failed: ${errorParam}`;
      setStatusMessage({ kind: 'error', text: friendly });
      clearGscQueryParams();
    }

    async function loadStatus() {
      try {
        const res = await authedFetch('/entity-status');
        if (res.ok) {
          const data = await res.json();
          setGscState(data.searchConsoleConnect ?? null);
          if (data.uid) {
            setEntityUid(data.uid);
          }
        }
      } catch (err) {
        console.error('[GSC UI] Error loading status:', err);
      } finally {
        setIsLoading(false);
      }
    }
    loadStatus();
  }, []);

  const handleConnect = () => {
    if (!entityUid) {
      alert('Unable to resolve entity ID. Please refresh and try again.');
      return;
    }

    // Path and param name confirmed correct against dashboardRouter's
    // actual /auth/gsc route (reads req.query.entityUid).
    window.location.href = `/dashboard/auth/gsc?entityUid=${encodeURIComponent(entityUid)}`;
  };

  const handleDisconnect = async () => {
  setIsDisconnecting(true);
  setStatusMessage(null);
  try {
    // CHANGED — authedFetch prefixes /dashboard/api/, but the disconnect
    // route lives on dashboardRouter (bare /dashboard), alongside
    // /auth/gsc/connect and /auth/gsc/callback — not on apiRouter.
    // authedFetch('/auth/gsc/disconnect', ...) was silently hitting
    // /dashboard/api/auth/gsc/disconnect, a route that doesn't exist —
    // confirmed by the missing [GSC Disconnect] server log and Firestore
    // showing connected: true unchanged after a "successful" disconnect.
    // authedFetchRaw takes a full /dashboard/... path and is exactly
    // what dashboardApi.ts's own header comment says to use for
    // non-/api dashboard routes.
    const res = await authedFetchRaw('/dashboard/auth/gsc/disconnect', { method: 'POST' });
    if (!res.ok) throw new Error('Failed to disconnect.');
    setGscState({ connected: false });
    setStatusMessage({ kind: 'success', text: 'Search Console disconnected.' });
  } catch (err: any) {
    setStatusMessage({ kind: 'error', text: err.message || 'Could not disconnect. Try again.' });
  } finally {
    setIsDisconnecting(false);
  }
};

  return (
    <div className="space-y-3 rounded-xl border border-brand-border bg-brand-white p-4">
      {statusMessage && (
        <div
          className={`flex items-center justify-between rounded-lg px-3 py-2 text-xs font-mono ${
            statusMessage.kind === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          <span>{statusMessage.text}</span>
          <button onClick={() => setStatusMessage(null)} aria-label="Dismiss">
            <XIcon className="size-3.5" />
          </button>
        </div>
      )}

      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2.5">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-brand-border bg-brand-surface">
            <GlobeIcon className="size-5 text-brand-charcoal" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-medium text-brand-black">Google Search Console</h4>
              {gscState?.connected ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-mono font-medium text-green-700 border border-green-200">
                  <span className="size-1.5 rounded-full bg-green-600" />
                  Connected
                </span>
              ) : (
                <span className="rounded-full bg-brand-surface px-2 py-0.5 text-xs font-mono text-brand-gray">
                  Not Connected
                </span>
              )}
            </div>
            <p className="mt-0.5 text-xs text-brand-gray">
              Allows AI agents to query search analytics and indexing performance.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {gscState?.connected && (
            <Button variant="ghost" size="sm" onClick={handleDisconnect} disabled={isDisconnecting}>
              {isDisconnecting ? <Loader2Icon className="size-3.5 animate-spin" /> : 'Disconnect'}
            </Button>
          )}
          <Button
            variant={gscState?.connected ? 'outline' : 'primary'}
            size="sm"
            onClick={handleConnect}
          >
            {gscState?.connected ? 'Re-authorize' : 'Connect Account'}
            <ExternalLinkIcon className="size-3.5 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}